import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import axios from 'axios';
import config from '../config';

const pick = (obj, ...keys) => {
    for (const k of keys) {
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
};

const isEmptyGuid = (g) => {
    const s = String(g ?? '').trim().toLowerCase();
    if (!s) return true;
    return s === '00000000-0000-0000-0000-000000000000' || s.startsWith('00000000');
};

const normalizePublicUser = (u) => {
    if (!u) return null;
    return {
        uid: pick(u, 'uid', 'Uid'),
        userName: pick(u, 'userName', 'UserName') ?? 'Unknown',
        isOnline: Boolean(pick(u, 'isOnline', 'IsOnline'))
    };
};

const getRawToken = () => {
    const t = localStorage.getItem('JWToken');
    if (!t) return null;
    return String(t).replace(/^Bearer\s+/i, '').trim();
};

const getAuthHeader = () => {
    const token = getRawToken();
    return {
        headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
        }
    };
};

function Lobby({ lobbyData, myUserId, onStartGame, onLeave, isLoading }) {
    const [activeTab, setActiveTab] = useState('lobby');

    const baseUrl = useMemo(() => {
        return config?.SERVER?.BASE_URL || config?.BASE_URL;
    }, []);

    const inviteCode = String(pick(lobbyData, 'inviteCode', 'InviteCode') ?? '');
    const status = String(pick(lobbyData, 'status', 'Status') ?? '');
    const creatorUid = String(pick(lobbyData, 'creator', 'Creator') ?? '');
    const joinedUid = String(pick(lobbyData, 'joined', 'Joined') ?? '');
    const lobbyId = pick(lobbyData, 'lobbyId', 'LobbyId');

    const isHost = Boolean(myUserId && creatorUid && String(creatorUid).toLowerCase() === String(myUserId).toLowerCase());
    const hasOpponent = Boolean(joinedUid && !isEmptyGuid(joinedUid));

    const [creatorUser, setCreatorUser] = useState(null);
    const [joinedUser, setJoinedUser] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadUser = async (uid, setter) => {
            const s = String(uid ?? '');
            if (!s || isEmptyGuid(s)) {
                setter(null);
                return;
            }

            try {
                const res = await axios.get(`${baseUrl}/evoSquare/User/Public/${s}`, getAuthHeader());
                if (cancelled) return;
                setter(normalizePublicUser(res.data));
            } catch {
                if (cancelled) return;
                setter(null);
            }
        };

        loadUser(creatorUid, setCreatorUser);
        loadUser(joinedUid, setJoinedUser);

        return () => {
            cancelled = true;
        };
    }, [baseUrl, creatorUid, joinedUid]);

    const defaultGameMinutes = useMemo(() => {
        try {
            const saved = localStorage.getItem('chess-settings');
            if (saved) {
                const s = JSON.parse(saved);
                const v = Number(s.timerMinutes);
                if (Number.isFinite(v) && v >= 0) return Math.floor(v);
            }
        } catch { }
        return 15;
    }, []);

    const [whitePlayerUid, setWhitePlayerUid] = useState('');
    const [maxAllowedGameTimeMinutes, setMaxAllowedGameTimeMinutes] = useState(defaultGameMinutes);
    const [startingFen, setStartingFen] = useState('');

    useEffect(() => {
        if (creatorUid) setWhitePlayerUid(creatorUid);
    }, [creatorUid]);

    const getDisplayName = (uid, info, emptyText) => {
        const s = String(uid ?? '');
        if (!s || isEmptyGuid(s)) return emptyText;

        const base = info?.userName || s;
        const isMe = myUserId && String(myUserId).toLowerCase() === s.toLowerCase();
        return `${base}${isMe ? ' (You)' : ''}`;
    };

    const renderPlayerLine = (iconClass, uid, info, emptyText, isMuted) => {
        const s = String(uid ?? '');
        const empty = !s || isEmptyGuid(s);

        const label = getDisplayName(s, info, emptyText);

        return (
            <div className="d-flex align-items-center mb-2">
                <i className={`${iconClass} me-2`}></i>
                <span
                    className={isMuted || empty ? "text-muted font-italic" : "text-white"}
                    style={{ display: 'flex', alignItemsCenter: 'center', gap: 8 }}
                >
                    {!empty && (
                        <span
                            title={info?.isOnline ? 'Online' : 'Offline'}
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                display: 'inline-block',
                                backgroundColor: info?.isOnline ? '#2ecc71' : 'rgba(255,255,255,0.35)'
                            }}
                        />
                    )}
                    {label}
                </span>
            </div>
        );
    };
    const canStart = isHost && hasOpponent;

    const submitStart = () => {
        if (!onStartGame) return;

        const req = {
            lobbyId: lobbyId,
            whitePlayerUid: whitePlayerUid || creatorUid,
            maxPlayerTimeMinutes: Math.max(0, Number(maxAllowedGameTimeMinutes) || 0),
            startingFen: String(startingFen || '').trim()
        };

        onStartGame(req);
    };

    if (!lobbyData) return null;

    const hostOptionLabel = getDisplayName(creatorUid, creatorUser, 'Host (Creator)');
    const guestOptionLabel = getDisplayName(joinedUid, joinedUser, 'Guest (Joined)');

    return (
        <div className="text-center">
            <h4 className="mb-3 text-white">Lobby</h4>

            <div className="bg-dark p-3 rounded border border-secondary mb-3 text-white">
                <p className="mb-1 text-muted">Invite Code</p>
                <div style={{ fontSize: '2rem', fontWeight: 900, letterSpacing: 4, color: '#B6FFDA' }}>
                    {inviteCode}
                </div>
                <div className="badge bg-info mt-2">{status}</div>
            </div>

            <div className="d-flex gap-2 mb-3 justify-content-center">
                <Button
                    size="sm"
                    variant={activeTab === 'lobby' ? 'primary' : 'outline-light'}
                    className={activeTab === 'lobby' ? 'bg-light-green txt-dark-green border-0' : ''}
                    onClick={() => setActiveTab('lobby')}
                >
                    Lobby
                </Button>
                {isHost && (
                    <Button
                        size="sm"
                        variant={activeTab === 'settings' ? 'primary' : 'outline-light'}
                        className={activeTab === 'settings' ? 'bg-light-green txt-dark-green border-0' : ''}
                        onClick={() => setActiveTab('settings')}
                    >
                        Game settings
                    </Button>
                )}
            </div>

            {activeTab === 'lobby' && (
                <div className="text-start px-2">
                    <h6 className="text-muted border-bottom pb-2">Players</h6>

                    {renderPlayerLine('bi bi-crown-fill text-warning', creatorUid, creatorUser, 'Host', false)}

                    {renderPlayerLine(
                        joinedUid && !isEmptyGuid(joinedUid) ? 'bi bi-person-check-fill text-success' : 'bi bi-person-dash text-secondary',
                        joinedUid,
                        joinedUser,
                        'Waiting for player...',
                        !hasOpponent
                    )}

                    <div className="mt-3 small text-muted">
                        {isHost ? "Ready to start when opponent joins." : "Waiting for the host to start the match."}
                    </div>

                    {isHost && (
                        <div className="d-grid gap-2 mt-4">
                            <Button
                                variant="primary"
                                className="menu-button txt-dark-green bg-light-green"
                                onClick={submitStart}
                                disabled={isLoading || !canStart}
                            >
                                {isLoading ? 'Starting...' : 'Start Game'}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'settings' && isHost && (
                <div className="text-start px-2">
                    <h6 className="text-muted border-bottom pb-2">Game setup</h6>

                    <div className="bg-dark p-3 rounded border border-secondary text-white">
                        <Form.Group className="mb-3">
                            <Form.Label>White side</Form.Label>
                            <Form.Select
                                className="bg-light text-dark"
                                value={whitePlayerUid}
                                onChange={(e) => setWhitePlayerUid(e.target.value)}
                            >
                                <option value={creatorUid}>{hostOptionLabel}</option>
                                {hasOpponent && <option value={joinedUid}>{guestOptionLabel}</option>}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label >Max match time (minutes)</Form.Label>
                            <Form.Control
                                type="number"
                                className="bg-light text-dark"
                                value={maxAllowedGameTimeMinutes}
                                onChange={(e) => setMaxAllowedGameTimeMinutes(Math.max(0, parseInt(e.target.value || '0', 10)))}
                                min={0}
                                defaultValue={10}
                            />
                            <div className="small mt-1">Use 0 for no limit.</div>
                        </Form.Group>

                        <Form.Group className="mb-0">
                            <Form.Label>Starting FEN (optional)</Form.Label>
                            <Form.Control
                                type="text"
                                className="bg-light text-dark"
                                placeholder="Leave empty for standard start position"
                                value={startingFen}
                                onChange={(e) => setStartingFen(e.target.value)}
                            />
                        </Form.Group>
                    </div>
                </div>
            )}

            <div className="d-grid gap-2 mt-4">
                <Button variant="outline-danger" onClick={onLeave}>
                    Leave Lobby
                </Button>
            </div>
        </div>
    );
}

export default Lobby;